import { v4 as uuidv4 } from 'uuid';
import { Timestamp } from 'firebase/firestore';
import { IResponse } from '../types/Response';
import { IRecordingWithAuthor } from '../types';
import { IRecording } from '../types/Recording';
import { IPin } from '../types/Pin';
import { Album } from '../types/Album';
import { generateAlbumKey } from '../utilities/albumKeyGenerator';

/**
 * Creates a timestamp object that survives JSON serialization
 * Uses Firestore format (seconds + nanoseconds) so it can be reconstructed
 */
export const createTimestamp = (date: Date) => ({
  seconds: Math.floor(date.getTime() / 1000),
  nanoseconds: (date.getTime() % 1000) * 1000000,
  toMillis: function() { return this.seconds * 1000 + this.nanoseconds / 1000000; },
  toDate: function() { return new Date(this.toMillis()); }
});

/**
 * Reconstructs a timestamp object from stored data (functions are lost in JSON)
 */
export const reconstructTimestamp = (ts: any) => {
  if (!ts) return null;
  // If it already has methods, it's already reconstructed
  if (typeof ts.toDate === 'function') return ts;
  // Reconstruct from seconds/nanoseconds (Firestore format)
  if (ts.seconds !== undefined) {
    const date = new Date(ts.seconds * 1000 + (ts.nanoseconds || 0) / 1000000);
    return {
      seconds: ts.seconds,
      nanoseconds: ts.nanoseconds || 0,
      toMillis: function() { return this.seconds * 1000 + this.nanoseconds / 1000000; },
      toDate: function() { return new Date(this.toMillis()); }
    };
  }
  // Handle old format that might have been stored as a number
  if (typeof ts === 'number') {
    const date = new Date(ts);
    return {
      seconds: Math.floor(ts / 1000),
      nanoseconds: (ts % 1000) * 1000000,
      toMillis: function() { return this.seconds * 1000 + this.nanoseconds / 1000000; },
      toDate: function() { return new Date(this.toMillis()); }
    };
  }
  // If timestamp is invalid or missing, return null to trigger regeneration
  return null;
};

/**
 * Creates dummy responses for demo mode
 * Only creates responses if there are recordings (pins exist)
 */
export const createDummyResponses = (firstRecording: IRecording): IResponse[] => {
  if (!firstRecording) {
    return [];
  }

  const now = new Date();
  const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  const twoDaysAgo = new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000);

  return [
    {
      key: uuidv4(),
      responseKey: uuidv4(),
      userKey: 'demo-user-1',
      recordingKey: firstRecording.key,
      recordingTitle: firstRecording.file?.title || 'Sample Recording',
      responseText: 'Connection is a state of mind, not a physical distance. Thank you.',
      timestamp: createTimestamp(oneDayAgo) as any,
      requiresModeration: false
      // No recordingPath or file - text-only response
    },
    {
      key: uuidv4(),
      responseKey: uuidv4(),
      userKey: 'demo-user-2',
      recordingKey: firstRecording.key,
      recordingTitle: firstRecording.file?.title || 'Sample Recording',
      // No responseText - audio-only response
      recordingPath: 'https://firebasestorage.googleapis.com/v0/b/placeholder-audio.appspot.com/o/placeholder-response-2.mp3?alt=media&token=placeholder',
      file: {
        recordingPath: 'https://firebasestorage.googleapis.com/v0/b/placeholder-audio.appspot.com/o/placeholder-response-2.mp3?alt=media&token=placeholder',
        title: 'User Response Audio',
        size: 1024000,
        audioLength: '00:45'
      },
      timestamp: createTimestamp(twoDaysAgo) as any,
      requiresModeration: true,
      moderationResult: {
        isAppropriate: false,
        flaggedCategories: ['language'],
        flaggedPhrases: ['inappropriate phrase']
      }
    }
  ];
};

/**
 * Creates a dummy submission for demo mode
 * Only creates submission if there are pins
 */
export const createDummySubmission = (firstPin: { pinKey: string; albumKey: string | null }, projectId: string): IRecordingWithAuthor | null => {
  if (!firstPin || !firstPin.albumKey) {
    return null;
  }

  const threeDaysAgo = Timestamp.fromMillis(Timestamp.now().toMillis() - 3 * 24 * 60 * 60 * 1000);

  return {
    key: uuidv4(),
    albumKey: firstPin.albumKey,
    pinKey: firstPin.pinKey,
    project: projectId,
    isUserRecording: true,
    isDemo: true,
    createdAt: threeDaysAgo,
    isSubmission: true, // Mark as a submission
    submittedAt: threeDaysAgo, // Timestamp when submitted
    approvalStatus: 'pending', // Default to pending for new submissions
      file: {
        recordingPath: 'recordings/demo/The Brook by Alfred Lord Tennyson_1.wav',
        title: 'The Brook by Alfred Lord Tennyson',
      description: 'This is a user-submitted recording. It needs to be reviewed and rated before being published.',
      size: 2048000,
      audioLength: '02:30'
    },
    ownership: {
      recordingAuthor: 'demo-author-1',
      recordingOwner: 'demo-user-3',
      recordingNarrator: 'demo-narrator-1'
    },
    tags: {
      subjectTags: ['user-submission', 'story'],
      genreTags: ['narrative']
    },
    requiresModeration: true,
    contentRating: undefined,
    author: {
      authorKey: 'demo-author-submission-1',
      name: 'Alfie T',
      bio: 'Local community member passionate about sharing stories and experiences. Enjoys participating in community art projects and documenting local history.',
      image: 'https://firebasestorage.googleapis.com/v0/b/placeholder-audio.appspot.com/o/placeholder-avatar.png?alt=media&token=placeholder',
      website: 'https://example.com/Alfie',
      social: '@Tenster',
      tags: {
        authorTags: ['community-member', 'storyteller', 'local-history']
      },
      isClaimed: true,
      userKey: 'demo-user-submission-1'
    }
  };
};

/**
 * Creates a dummy pin with Birmingham location for demo mode
 * Also creates an associated album
 * Returns both the pin and album
 */
/**
 * Creates a dummy pin with Birmingham location for demo mode
 * Also creates an associated album
 * Returns both the pin and album
 * Uses a localStorage flag to prevent duplicate creation
 */
export const createDummyPinWithAlbum = async (projectId: string): Promise<{ pin: IPin; album: Album } | null> => {
  try {
    // Check if we're already creating a pin for this project (prevent race conditions)
    const creationKey = `demo_pin_creation_${projectId}`;
    const isCreating = localStorage.getItem(creationKey);
    
    if (isCreating === 'true') {
      console.log('[createDummyPinWithAlbum] Pin creation already in progress, skipping');
      return null;
    }
    
    // Set flag to prevent duplicate creation
    localStorage.setItem(creationKey, 'true');
    
    try {
      // Create an album for the pin
      const albumKey = await generateAlbumKey();
      const album: Album = {
        albumKey,
        projectKey: projectId,
        name: 'Demo Album',
        userKey: null
      };
      
      // Create a dummy pin with Birmingham location
      const pinKey = uuidv4();
      const pin: IPin = {
        pinKey,
        name: 'Birmingham Demo Pin',
        pinDescription: 'A demo pin in Birmingham for testing submissions',
        pinIcon: '/assets/icons/overhearlight.png', // Use absolute path for local assets
        isAvailable: true,
        location: {
          geoPoint: {
            lat: 52.4862, // Birmingham coordinates
            long: -1.8904
          },
          radius: null
        },
        albumKey,
        project: projectId,
        pinType: 'qrCode',
        isCollectionLogicEnabled: false,
        collectionLogic: null,
        pinOrder: null,
        branchParentPinKey: null,
        qrDeliveryType: 'single',
        qrPath: null,
        tags: {
          locationTags: ['Birmingham', 'demo']
        },
        isDemo: true
      };
      
      console.log('[createDummyPinWithAlbum] Created dummy pin', { 
        pinKey, 
        albumKey, 
        projectId,
        pinName: pin.name 
      });
      
      return { pin, album };
    } finally {
      // Clear the flag after a short delay to allow the pin to be saved
      setTimeout(() => {
        localStorage.removeItem(creationKey);
      }, 1000);
    }
  } catch (error) {
    console.error('Error creating dummy pin:', error);
    const creationKey = `demo_pin_creation_${projectId}`;
    localStorage.removeItem(creationKey);
    return null;
  }
};

